import React, { useContext, useState } from 'react';
// material
import {
  Stack,
  Container,
  Typography,
  Card,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Divider,
  Fab,
  Button,
  Tooltip
} from '@mui/material';
import { Icon } from '@iconify/react';
import trashFill from '@iconify/icons-eva/trash-2-fill';
import editFill from '@iconify/icons-eva/edit-2-fill';
import plusFill from '@iconify/icons-eva/plus-fill';
import { grey } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
// components
import TicketFormCreateDialog from '../ticket/form/create/dialog';
import TicketList from '../ticket/list';
import SectionFormDialog from './form/dialog';
// contexts
import { EventContext } from '../../contexts/Event';
import { TicketsContext } from '../../contexts/Tickets';
import { useAuth } from '../../contexts/Auth';
import { SettingsContext } from '../../contexts/Settings';
// services
import { deleteSection } from '../../services/Section';

const CardStyle = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    border: `1px dashed ${grey[300]}`
  }
}));

export default function SectionList() {
  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);
  const { event } = useContext(EventContext);
  const { sections, setSections, tickets } = useContext(TicketsContext);

  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [focusTicket, setFocusTicket] = useState();
  const [openTicketFormCreateDialog, setOpenTicketFormCreateDialog] = useState(false);
  const [focusSection, setFocusSection] = useState();
  const [openSectionFormDialog, setOpenSectionFormDialog] = useState(false);

  const handleOpenTicketCreateDialog = (ticket) => {
    setFocusTicket(ticket);
    setOpenTicketFormCreateDialog(true);
  };

  const handleOpenSectionFormDialog = (section) => {
    setFocusSection(section);
    setOpenSectionFormDialog(true);
  };

  const handleSectionDelete = async (section) => {
    if (!window.confirm(`Do you really want to delete this section: "${section.name}"?`)) {
      return;
    }

    setIsLoading(true);

    try {
      const idToken = await authUser.getIdToken();
      const headers = {
        Authorization: `Bearer ${idToken}`,
        'Account-Key': authSettings.server.path
      };

      await deleteSection({ headers, eventId: event._id, sectionId: section._id });
      setSections((sections) =>
        sections.filter((currentSection) => currentSection._id !== section._id)
      );
    } catch (err) {
      console.error(err);
      setHasError(true);
    }

    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (hasError) {
    return (
      <Alert severity="error">An error occurred! Please, refresh the page and try again.</Alert>
    );
  }

  // TODO: Ficou muito grande o código. Dividir em components menores
  return (
    <>
      <Container>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'start', sm: 'center' }}
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4" sx={{ mb: { xs: 1, sm: 0 } }}>
            Tickets
          </Typography>
          {sections.length > 0 && (
            <Stack spacing={2} direction="row" alignItems="center" justifyContent="center">
              {!sections[0].single && (
                <Button variant="outlined" size="large" onClick={handleOpenSectionFormDialog}>
                  Create Section
                </Button>
              )}
              <Button variant="contained" size="large" onClick={handleOpenTicketCreateDialog}>
                Create Ticket
              </Button>
            </Stack>
          )}
        </Stack>
        {sections.length ? (
          <>
            {!sections[0].single ? (
              <List>
                {sections.map((section) => (
                  <Card sx={{ mb: 3 }} key={section._id}>
                    <ListItem
                      sx={{ p: { xs: 2, sm: 3 } }}
                      secondaryAction={
                        <>
                          <IconButton
                            aria-label="edit"
                            onClick={() => handleOpenSectionFormDialog(section)}
                          >
                            <Icon icon={editFill} />
                          </IconButton>
                          {tickets[section._id]?.length ? (
                            <Tooltip
                              title="You can't delete this section because it has tickets associated. Delete all tickets first!"
                              placement="bottom-end"
                            >
                              <span>
                                <IconButton aria-label="delete" disabled>
                                  <Icon icon={trashFill} />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <IconButton
                              aria-label="delete"
                              onClick={() => handleSectionDelete(section)}
                            >
                              <Icon icon={trashFill} />
                            </IconButton>
                          )}
                        </>
                      }
                    >
                      <ListItemText
                        sx={{ width: { xs: '20%', sm: '30%' } }}
                        primary={section.name}
                        secondary={section.description || <i>Empty description</i>}
                      />
                      {/* TODO: A capacidade tem que pegar do inventário, pois se não editar,
                        é possível saber (Precisa fazer verificação se o inventario existe primeiro)
                        OBS: Fazer isso também nos tickets
                       */}
                      <ListItemText primary="Capacity" secondary={section.capacity} />
                    </ListItem>

                    <Divider />

                    {tickets[section._id]?.length ? (
                      <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        <TicketList section={section} />
                        <Box sx={{ textAlign: 'center' }}>
                          <Fab
                            size="medium"
                            color="primary"
                            aria-label="add"
                            sx={{ flexShrink: 0 }}
                            onClick={() => handleOpenTicketCreateDialog({ section })}
                          >
                            <Icon icon={plusFill} width="24" />
                          </Fab>
                        </Box>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', p: 5 }}>
                        <Typography variant="h5" sx={{ mb: 2 }}>
                          No tickets yet!
                          <Typography
                            component="span"
                            variant="body2"
                            color="grey.500"
                            sx={{ display: 'block' }}
                          >
                            Create your first ticket
                          </Typography>
                        </Typography>
                        <Fab
                          size="medium"
                          color="primary"
                          aria-label="add"
                          sx={{ flexShrink: 0 }}
                          onClick={() => handleOpenTicketCreateDialog({ section })}
                        >
                          <Icon icon={plusFill} width="24" />
                        </Fab>
                      </Box>
                    )}
                  </Card>
                ))}
              </List>
            ) : (
              <>
                {sections.map((section) => (
                  <TicketList key={section._id} section={section} />
                ))}
              </>
            )}
          </>
        ) : (
          <CardStyle>
            <Box sx={{ py: 5, textAlign: 'center' }}>
              {event.bookingType === 'SEATSIO' ? (
                <>
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    No tickets registered!
                    <Typography
                      component="span"
                      variant="body2"
                      color="grey.500"
                      sx={{ display: 'block', maxWidth: '300px', mx: 'auto' }}
                    >
                      First you need to create sections and add the Seats Category Key, then create
                      your ticket
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      color="grey.500"
                      sx={{ display: 'block', maxWidth: '300px', mx: 'auto' }}
                    >
                      i.e. Floor, Mezzanine.
                    </Typography>
                  </Typography>

                  <Stack spacing={2} direction="row" alignItems="center" justifyContent="center">
                    <Button variant="outlined" size="large" onClick={handleOpenSectionFormDialog}>
                      Create Section
                    </Button>
                  </Stack>
                </>
              ) : (
                <>
                  <Typography variant="h5" sx={{ mb: 2 }}>
                    No tickets registered!
                    <Typography
                      component="span"
                      variant="body2"
                      color="grey.500"
                      sx={{ display: 'block', maxWidth: '300px', mx: 'auto' }}
                    >
                      Create a section if you need to group tickets and share the same inventory in
                      a single area.
                    </Typography>
                    <Typography
                      component="span"
                      variant="body2"
                      color="grey.500"
                      sx={{ display: 'block', maxWidth: '300px', mx: 'auto' }}
                    >
                      i.e. Floor, Mezzanine.
                    </Typography>
                  </Typography>

                  <Stack
                    spacing={{ xs: 1, sm: 2 }}
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Button variant="outlined" size="large" onClick={handleOpenSectionFormDialog}>
                      Create Section
                    </Button>
                    <Typography
                      component="span"
                      variant="body2"
                      color="grey.500"
                      sx={{ display: 'block', mt: 2 }}
                    >
                      or
                    </Typography>
                    <Button variant="contained" size="large" onClick={handleOpenTicketCreateDialog}>
                      Create Ticket
                    </Button>
                  </Stack>
                </>
              )}
            </Box>
          </CardStyle>
        )}
      </Container>

      <TicketFormCreateDialog
        openTicketFormCreateDialog={openTicketFormCreateDialog}
        setOpenTicketFormCreateDialog={setOpenTicketFormCreateDialog}
        ticket={focusTicket}
      />

      <SectionFormDialog
        openSectionFormDialog={openSectionFormDialog}
        setOpenSectionFormDialog={setOpenSectionFormDialog}
        section={focusSection}
      />
    </>
  );
}
